import { IconNote, IconTiers } from '../../../common/icons';
import Picker from '../../../common/components/picker/picker';
import { searchProjects } from '../../projects/contexts/projects';
import {
    EXPECTED_OUTCOME_MAX, NAME_MAX, STATUS_OPTIONS, TIER_STATE_OPTIONS,
} from '../utils/form';

import '../../clients/styles/form.css';

// `projectLocked` is true while editing: PATCH does not take project_id, so a
// tier cannot change projects from here. The field still shows — hiding it
// would leave the screen without saying which project this belongs to — but it
// cannot be changed.

export default function TierFields({
    values, errors = {}, onChange, disabled, projectLocked, projectLabel,
}) {
    const set = (field) => (e) => onChange(field, e.target.value);
    const state = TIER_STATE_OPTIONS.find(option => option.value === values.tier_state);

    return (
        <div className="client-form__grid">
            <label className="client-form__field">
                <span className="client-form__label">
                    Proyecto <span className="client-form__required">*</span>
                </span>
                <span className="client-form__control">
                    <Picker
                        value={values.project_id}
                        onChange={(id) => onChange('project_id', id)}
                        fetchOptions={searchProjects}
                        cacheKey="projects"
                        labelOf={project => project.name}
                        subtitleOf={project => project.slug}
                        selectedLabel={projectLabel}
                        placeholder="Elegir proyecto..."
                        disabled={disabled || projectLocked}
                        invalid={!!errors.project_id}
                    />
                </span>
                {errors.project_id
                    ? <span className="client-form__error">{errors.project_id}</span>
                    : projectLocked && (
                        <span className="client-form__note">
                            Un tier no cambia de proyecto: se crea en el suyo.
                        </span>
                    )}
            </label>

            <label className="client-form__field">
                <span className="client-form__label">Estado del tier</span>
                <span className="client-form__control">
                    <select
                        className="client-form__input client-form__input--select"
                        value={values.tier_state}
                        onChange={set('tier_state')}
                        disabled={disabled}
                    >
                        {TIER_STATE_OPTIONS.map(option => (
                            <option key={option.value} value={option.value}>{option.label}</option>
                        ))}
                    </select>
                </span>
                {errors.tier_state
                    ? <span className="client-form__error">{errors.tier_state}</span>
                    : state && <span className="client-form__note">{state.hint}</span>}
            </label>

            <label className="client-form__field">
                <span className="client-form__label">
                    Nombre <span className="client-form__required">*</span>
                </span>
                <span className="client-form__control">
                    <input
                        className={`client-form__input ${errors.name ? 'client-form__input--invalid' : ''}`}
                        type="text"
                        value={values.name}
                        onChange={set('name')}
                        maxLength={NAME_MAX}
                        placeholder="Fundación, núcleo de trabajo, contrato de API..."
                        disabled={disabled}
                        required
                    />
                    <span className="client-form__icon" aria-hidden="true"><IconTiers /></span>
                </span>
                {errors.name && <span className="client-form__error">{errors.name}</span>}
            </label>

            <label className="client-form__field">
                <span className="client-form__label">Orden</span>
                <span className="client-form__control">
                    <input
                        className={`client-form__input ${errors.sort_order ? 'client-form__input--invalid' : ''}`}
                        type="number"
                        min={0}
                        value={values.sort_order}
                        onChange={set('sort_order')}
                        disabled={disabled}
                    />
                </span>
                {errors.sort_order
                    ? <span className="client-form__error">{errors.sort_order}</span>
                    : (
                        <span className="client-form__note">
                            En qué lugar va dentro del proyecto. El menor primero.
                        </span>
                    )}
            </label>

            <label className="client-form__field client-form__field--wide">
                <span className="client-form__label">Descripción</span>
                <span className="client-form__control">
                    <textarea
                        className={`client-form__input client-form__input--area ${errors.description ? 'client-form__input--invalid' : ''}`}
                        value={values.description}
                        onChange={set('description')}
                        rows={4}
                        placeholder="El objetivo del tier y cuál es su compuerta"
                        disabled={disabled}
                    />
                    <span className="client-form__icon client-form__icon--top" aria-hidden="true">
                        <IconNote />
                    </span>
                </span>
                {errors.description
                    ? <span className="client-form__error">{errors.description}</span>
                    : <span className="client-form__note">El alcance y las exclusiones: qué entra y qué no.</span>}
            </label>

            {/* `description` says what is in scope; `expected_outcome` says what
                has to EXIST when the tier ends. They are two columns in the
                contract and two questions in a review, and a tier cannot be
                agreed without both. */}
            <label className="client-form__field client-form__field--wide">
                <span className="client-form__label">Resultado esperado</span>
                <span className="client-form__control">
                    <textarea
                        className={`client-form__input client-form__input--area ${errors.expected_outcome ? 'client-form__input--invalid' : ''}`}
                        value={values.expected_outcome}
                        onChange={set('expected_outcome')}
                        rows={4}
                        maxLength={EXPECTED_OUTCOME_MAX}
                        placeholder="Qué tiene que existir cuando el tier termine"
                        disabled={disabled}
                    />
                    <span className="client-form__icon client-form__icon--top" aria-hidden="true">
                        <IconNote />
                    </span>
                </span>
                {errors.expected_outcome
                    ? <span className="client-form__error">{errors.expected_outcome}</span>
                    : <span className="client-form__note">Hace falta para acordar el tier.</span>}
            </label>

            {/* ZERO OPEN QUESTIONS IS A CONDITION OF AGREEING, so this box being
                empty is a milestone and not a blank field. The note says it,
                because an empty textarea reads as "nobody filled this in". */}
            <label className="client-form__field client-form__field--wide">
                <span className="client-form__label">Dudas abiertas</span>
                <span className="client-form__control">
                    <textarea
                        className={`client-form__input client-form__input--area ${errors.open_questions ? 'client-form__input--invalid' : ''}`}
                        value={values.open_questions}
                        onChange={set('open_questions')}
                        rows={3}
                        placeholder={'Una por línea.\n¿El exportador corre en el mismo proceso?'}
                        disabled={disabled}
                    />
                </span>
                {errors.open_questions
                    ? <span className="client-form__error">{errors.open_questions}</span>
                    : (
                        <span className="client-form__note">
                            Una por línea. Un tier se acuerda con <strong>cero</strong> dudas:
                            vaciar esta caja es parte del trabajo, no un descuido.
                        </span>
                    )}
            </label>

            <label className="client-form__field">
                <span className="client-form__label">Estado del registro</span>
                <span className="client-form__control">
                    <select
                        className="client-form__input client-form__input--select"
                        value={values.status}
                        onChange={set('status')}
                        disabled={disabled}
                    >
                        {STATUS_OPTIONS.map(option => (
                            <option key={option.value} value={option.value}>{option.label}</option>
                        ))}
                    </select>
                </span>
                {errors.status && <span className="client-form__error">{errors.status}</span>}
            </label>
        </div>
    );
}
